import { Layout, Menu } from "antd";
import PropTypes from "prop-types";
import { useContext, useState } from "react";
import { useNavigate } from "react-router";
import LockedModuleTooltip, {
	noSubscriptionMessage,
} from "../components/LockedModuleTooltip";
import { HomeContext } from "../containers/Home";
import useCheckModule from "../hooks/useCheckModule";
import usePermission from "../hooks/usePermission";
import { useTenantStore } from "../stores/tenantStore";
import { useUserStore } from "../stores/userStore";
import { usePermissionStore } from "../stores/permissionStore";
import { superAdminRole } from "../utils/_exports";
import { GetAntIcon } from "../utils/ant_icons";
import { setHomeLogo } from "../utils/company_logo";
import Emitter from "../utils/emitter";
import { HELP_CENTRE_PATH } from "../utils/helpCentre/content";
import {
	checkSuperAdminPriviledge,
	includesIgnoreCase,
	logout,
} from "../utils/utils";
const { Sider } = Layout;

/** Main menu entries an examiner-only session keeps. */
const EXAMINER_MENU_KEYS = ["dashboard-menu", "project-menu"];

export const MainMenuItems = () => {
	const context: any = useContext(HomeContext);
	const allowedViewProjects = usePermission("View.Project");
	const isExaminerOnly = usePermissionStore(
		(state) => !!state.accessProfile?.restricted
	);

	const isSuperAdmin = context.userInfo?.userRole.includes(
		import.meta.env.VITE_SUPER_ADMIN_TAG
	);
	const hasTenantRole = !!context.userRole && !Array.isArray(context.userRole);
	const isGuest =
		!isSuperAdmin && hasTenantRole && context.userRole.role === "guest";

	const initialMainMenu: any = [
		{
			key: "dashboard-menu",
			label: "Dashboard",
			proptitle: "Dashboard",
			container: "dashboard",
			icon: GetAntIcon("global"),
			to: "/dashboard",
			module_key: "core",
		},
		{
			key: "project-menu",
			label: "Projects",
			proptitle: "Projects",
			container: "projects",
			icon: GetAntIcon("project"),
			ispermissible: allowedViewProjects.hasPermission,
			to: "/projects",
			module_key: "core",
			child: [
				{
					proptitle: "Add new project",
					to: "/add-new-project",
					key: "add-new-project",
				},
				{
					proptitle: "Project settings",
					to: "/project-settings",
					key: "project-settings",
				},
				{
					proptitle: "Operation settings",
					to: "/operation-settings",
					key: "Operation settings",
				},
			],
		},
		// {
		// 	key: "well-menu",
		// 	label: "Wells",
		// 	proptitle: "Wells",
		// 	container: "wells",
		// 	icon: GetAntIcon("well-icon"),
		// 	ispermissible: allowedViewWells,
		// 	to: "/wells",
		// 	module_key: "core",
		// 	child: [
		// 		{
		// 			proptitle: "Well settings",
		// 			to: "/well-settings",
		// 			key: "Well settings",
		// 		},
		// 	],
		// },
		{
			key: "reporting-menu",
			label: "Reporting",
			proptitle: "Reporting",
			container: "reporting",
			icon: GetAntIcon("file2"),
			ispermissible: true,
			to: "/reporting",
			child: [
				{
					proptitle: "Report overview",
					to: "/report-overview",
					key: "Report overview",
				},
				{
					proptitle: "Audit stream",
					to: "/audit-stream",
					key: "Audit stream",
				},
				{
					proptitle: "Incident stream",
					to: "/incident-stream",
					key: "Incident stream",
				},
			],
			module_key: "reporting",
		},
		{
			key: "lessons-learned-menu",
			label: "Lessons learned",
			proptitle: "Lessons learned",
			container: "lesson-learned-stream",
			icon: GetAntIcon("file-done"),
			isPermissible: true,
			to: "/lessons-learned",
			child: [
				{
					proptitle: "Lesson learned details",
					to: "/lesson-learned-details",
					key: "Lesson learned details",
				},
			],
			module_key: "lessons-learned",
		},
		// Waste time do timewriting no use
		{
			key: "timewriting-menu",
			label: "Time writing",
			tooltip: noSubscriptionMessage,
			proptitle: "Time writing",
			container: "timewriting",
			icon: GetAntIcon("timewriting"),
			ispermissible: true,
			to: "/timewriting",
			module_key: "time-writing",
		},
		// {
		// 	key: "chemical-management-menu",
		// 	label: "Chemical management",
		// 	proptitle: "Chemical management",
		// 	container: "chemicalManagement",
		// 	icon: GetAntIcon("experiment"),
		// 	ispermissible: true,
		// 	to: "/chemical-management",
		// 	module_key: "chemical-management",
		// },
	];

	const initialExtraMenu: any = [
		// Help Menu Button
		{
			key: "help-menu",
			label: "Help Centre",
			proptitle: "Help Centre",
			className: "extra-menu-item",
			container: "helpCentre",
			to: HELP_CENTRE_PATH,
			icon: GetAntIcon("question3"),
			ispermissible: !isGuest,
		},
		// Administration Button
		{
			key: "admin-menu",
			label: "Administration",
			proptitle: "Administration",
			className: "extra-menu-item",
			container: "admin",
			to: "/administration",
			icon: GetAntIcon("setting"),
			requires_admin: true,
			child: [
				{
					proptitle: "Programme settings",
					to: "/programme-settings",
					key: "Programme settings",
				},
				{
					proptitle: "Tenant configuration",
					to: "/tenant-configuration",
					key: "Tenant configuration",
				},
			],
		},
	];

	const otherMenu: any = [
		{
			key: "user-profile",
			label: "My profile",
			proptitle: "My Profile",
			container: "profile",
			to: "/profile",
		},
		{
			key: "my-tasks",
			label: "My Tasks",
			proptitle: "My Tasks",
			container: "my-tasks",
			to: "/my-tasks",
		},
	];

	if (isExaminerOnly) {
		// External well examiners only get the dashboard, their projects and
		// their profile; the server rejects every other tenant API anyway.
		return {
			initialMainMenu: initialMainMenu.filter((item: any) =>
				EXAMINER_MENU_KEYS.includes(item.key)
			),
			initialExtraMenu: [],
			otherMenu: otherMenu.filter((item: any) => item.key === "user-profile"),
		};
	}

	return { initialMainMenu, initialExtraMenu, otherMenu };
};

const MainMenu = (props: any) => {
	const context: any = useContext(HomeContext);
	const navigate = useNavigate();
	const [selectedKeys, setSelectedKeys] = useState<any>([]);

	const userStore = useUserStore();
	const currentUser = userStore.getCurrentUser();
	const userEmail = currentUser?.email?.toLowerCase();
	const userRoles = userStore.getUserRole();

	const currentTenant = useTenantStore((s) => s.tenant);

	const { initialMainMenu, initialExtraMenu } = MainMenuItems();

	const { checkModule } = useCheckModule();

	// Render Menu
	const renderMenu = () => {
		const filterChildren = (children: any[], roles: any) => {
			return children
				.map((child) =>
					child.requires_admin && !roles?.includes(superAdminRole)
						? null
						: child
				)
				.filter(Boolean);
		};

		const lockMenuLabel = (label: string) => {
			return (
				<>
					{props.collapsed ? (
						noSubscriptionMessage
					) : (
						<LockedModuleTooltip>{label}</LockedModuleTooltip>
					)}
				</>
			);
		};

		const createMainMenu = (roles: any) => {
			if (!context.userInfo) return [];

			const email = context.userInfo.user.email;
			const isAdmin =
				roles?.includes(superAdminRole) ||
				includesIgnoreCase(context?.currentTenantData?.tenant_admin, email);

			return initialMainMenu.map((menuItem: any) => {
				const allowAccess = checkModule(menuItem.module_key);

				// Handle ispermissible flag
				if ("ispermissible" in menuItem && !menuItem.ispermissible) {
					return null;
				}

				// Handle requires_admin at parent level
				if (menuItem.requires_admin && !isAdmin) {
					return null;
				}

				// Handle children filtering
				if (menuItem.children) {
					const filteredChildren = filterChildren(menuItem.children, roles);
					if (filteredChildren.length === 0) return null;

					return {
						...menuItem,
						disabled: !allowAccess,
						children: filteredChildren,
					};
				}

				// return nothing if user is not authorized to access the module
				if (
					["reporting", "lessons-learned"].includes(menuItem.module_key) &&
					!checkSuperAdminPriviledge(userRoles)
				) {
					const userIsAuthorizedToAccessModule = includesIgnoreCase(
						currentTenant?.modules_authorized_users?.[menuItem.module_key],
						userEmail
					);
					if (!userIsAuthorizedToAccessModule) return null;
				}

				// Default case
				return {
					...menuItem,
					disabled: !allowAccess,
					label: allowAccess ? menuItem.label : lockMenuLabel(menuItem.label),
				};
			});
		};

		const handleMainMenu = async (info: any) => {
			try {
				await context.handlePageChange();
				["main-tabkey", "admin-tabkey", "secondary-tabkey", "tabkey"].forEach(
					(key) => sessionStorage.removeItem(key)
				);

				if (info.item.props.to) {
					setSelectedKeys([info.key]);
					Emitter.emit("menuChange", [info.item.props]);
					navigate(info.item.props.to);
				}
			} catch {}
		};

		return (
			<Menu
				className="main-menu"
				mode="inline"
				defaultSelectedKeys={["dashboard-menu"]}
				selectedKeys={props.selectedKey || selectedKeys}
				items={createMainMenu(props.roles)}
				onClick={handleMainMenu}
			/>
		);
	};

	// Render Extra Menu
	const renderExtraMenu = () => {
		// Inner function to create the filtered extra menu based on roles
		const createExtraMenu = (roles: any) => {
			// Map through `initialExtraMenu` to create menu items
			return initialExtraMenu.map((menuItem: any) => {
				if ("ispermissible" in menuItem) {
					const ispermissible = menuItem.ispermissible;
					if (!ispermissible) {
						return null;
					} else {
						// Check if the menu item has children
						if (menuItem.children) {
							// Filter the child items based on requires_admin
							const filteredChildren = menuItem.children
								.map((childItem: any) => {
									if (childItem.requires_admin) {
										return roles?.includes(superAdminRole) ? childItem : null;
									}
									return childItem;
								})
								.filter(Boolean); // Remove nulls

							// Only return the parent if it has visible children
							if (filteredChildren.length > 0) {
								return { ...menuItem, children: filteredChildren };
							}
							return null;
						}

						// Return the menuItem if no specific requires_admin logic is applied
						return menuItem;
					}
				}

				// If menuItem requires admin and user is not a super-admin, exclude this menuItem
				if (
					menuItem.requires_admin &&
					!roles?.includes(superAdminRole) &&
					!roles?.includes("tenant-admin")
				) {
					return null;
				}

				// Check if the menuItem has child items (submenu)
				if (menuItem.children) {
					// Filter the children based on admin requirements
					const filteredChildren = menuItem.children
						.map((childItem: any) => {
							// Only include child items requiring admin if user has the necessary role
							if (childItem.requires_admin) {
								return roles?.includes(superAdminRole) ||
									roles?.includes("tenant-admin")
									? childItem
									: null;
							}
							return childItem; // Include child if no admin requirement
						})
						.filter(Boolean); // Remove any nulls from filtered children

					// Return the parent item only if it has visible children
					if (filteredChildren.length > 0) {
						return { ...menuItem, children: filteredChildren };
					}
					return null; // Exclude the parent if no visible children
				}

				// Return the menuItem if no specific requires_admin logic is applied
				return menuItem;
			});
		};

		// Function to handle actions when a menu item is clicked
		const handleExtraMenu = async (info: any) => {
			try {
				await context.handlePageChange(); // Handle page change context
				sessionStorage.removeItem("admin-tabkey"); // Clear session storage for admin-tabkey
				sessionStorage.removeItem("secondary-tabkey"); // Clear session storage for secondary-tabkey
				sessionStorage.removeItem("tabkey"); // Clear session storage for tabkey

				// Navigate to a new page if the menu item has a `to` property
				if (info.item.props.to) {
					Emitter.emit("menuChange", [info.item.props]); // Emit menu change event
					setSelectedKeys([info.key]); // Update selected menu key
					navigate(info.item.props.to);
				}

				// If `logout` is clicked
				if (info.key === "logout") {
					localStorage.removeItem("idToken"); // Remove idToken
					localStorage.removeItem("accessToken"); // Remove accessToken
					logout(); // Call logout function
				}

				// If `manage-account` is clicked
				if (info.key === "manage-account") {
					window.open(
						"https://myaccount.microsoft.com/?ref=MeControl",
						"_blank",
						"noopener,noreferrer"
					);
				}
			} catch (error) {
				console.error(error);
			}
		};

		return (
			<Menu
				selectedKeys={props.selectedKey || selectedKeys}
				className="extra-menu"
				style={{
					marginTop: "auto",
				}}
				mode="inline"
				items={createExtraMenu(props.roles)}
				onClick={(info: any) => {
					handleExtraMenu(info);
				}}
			/>
		);
	};

	return (
		<>
			{props.option != "Mega" ? (
				<Sider
					className="main-sider"
					collapsed={props.collapsed}
					onCollapse={props.onCollapse}
					collapsible
				>
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							marginTop: "20px",
							marginBottom: "15px",
						}}
					>
						{setHomeLogo()}
					</div>
					{renderMenu()}
					{renderExtraMenu()}
				</Sider>
			) : (
				<></>
			)}
		</>
	);
};

MainMenu.propTypes = {
	collapsed: PropTypes.bool,
	onCollapse: PropTypes.func,
	option: PropTypes.any,
	roles: PropTypes.array,
	selectedKey: PropTypes.any,
};

export default MainMenu;
